import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Upload, Image as ImageIcon, FileText, Trash2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export const MediaLibrary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (user && !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchMedia();
  }, [user, navigate]);

  const fetchMedia = async () => {
    try {
      const response = await api.get('/media/');
      setMedia(response.data);
    } catch (error) {
      toast.error('Error al cargar medios');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedFiles = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/media/upload/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedFiles.push(response.data);
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }

    if (uploadedFiles.length > 0) {
      toast.success(`${uploadedFiles.length} archivo(s) subido(s)`);
      fetchMedia();
    }

    setUploading(false);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleUpload(files);
  }, []);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleUpload(files);
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) return;

    try {
      await api.delete(`/media/${mediaId}/`);
      toast.success('Archivo eliminado');
      fetchMedia();
    } catch (error) {
      toast.error('Error al eliminar archivo');
    }
  };

  const copyToClipboard = (url, id) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(id);
    toast.success('URL copiada al portapapeles');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getFileIcon = (type) => {
    if (type === 'image') return <ImageIcon className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="media-library-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">
            Biblioteca de Medios
          </h1>
          <p className="text-muted-foreground">Sube y gestiona imágenes y archivos</p>
        </div>

        {/* Upload Area */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Subir Archivos</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragActive
                  ? 'border-accent bg-accent/5'
                  : 'border-border hover:border-accent/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                Arrastra archivos aquí o haz clic para seleccionar
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Soporta: JPG, PNG, GIF, PDF (máx. 10MB por archivo)
              </p>
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileInput}
                className="hidden"
                id="file-input"
                disabled={uploading}
              />
              <label htmlFor="file-input">
                <Button disabled={uploading} asChild>
                  <span>{uploading ? 'Subiendo...' : 'Seleccionar Archivos'}</span>
                </Button>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Media Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.length === 0 ? (
            <div className="col-span-full">
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">No hay archivos subidos aún</p>
                </CardContent>
              </Card>
            </div>
          ) : (
            media.map((item) => (
              <Card key={item.id} className="group hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  {/* Preview */}
                  <div className="aspect-square bg-muted rounded-lg mb-3 overflow-hidden relative">
                    {item.type === 'image' ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {getFileIcon(item.type)}
                      </div>
                    )}
                    {/* Delete Button Overlay */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(item.id)}
                        data-testid={`delete-media-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium truncate" title={item.filename}>
                      {item.filename}
                    </p>
                    <Badge variant="secondary" className="text-xs">
                      {item.type}
                    </Badge>
                    
                    {/* Copy URL Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => copyToClipboard(item.url, item.id)}
                      data-testid={`copy-url-${item.id}`}
                    >
                      {copiedId === item.id ? (
                        <>
                          <Check className="w-3 h-3 mr-2" />
                          Copiado
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3 mr-2" />
                          Copiar URL
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;
